/**
 * ModelResultsView — the production workspace's "Results" tab (dashboard unit 4).
 *
 * Renders the model's generated results dashboard. The dashboard is
 * AGENT-AUTHORED HTML, so it renders with ZERO app privileges:
 *   - `<iframe sandbox="allow-scripts">` — NO `allow-same-origin`, so the frame
 *     runs in an opaque origin and cannot reach app cookies / localStorage /
 *     the parent DOM / auth tokens.
 *   - A defence-in-depth CSP `<meta>` is injected (default-src 'none';
 *     connect-src 'none') so the baked-in dashboard cannot phone home /
 *     exfiltrate, while still allowing the self-contained inline scripts/styles.
 *   - The HTML is fetched as a STRING and rendered via `srcDoc` (never an iframe
 *     `src=` that would share the app origin).
 *
 * The HTML itself lives in the canonical artifact store; the `dashboard_html`
 * DocFile (doc_kind) in workspace state carries the run binding + provenance.
 * Chrome uses DESIGN.md tokens only; the iframe CONTENT is agent-authored and
 * out of scope for the token rule (like third-party styles).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useProcessViewerStore } from '../../stores/processViewerStore';
import api from '../../apiService';
import { BarChart3, Loader2, AlertCircle, RefreshCw, ShieldCheck, Clock } from 'lucide-react';
import type { DictDocFile } from '../../types/workspaceState';
import { formatDateTime } from '../../utils/formatDateTime';

interface ModelResultsViewProps {
  projectId: string | null;
  authToken?: string;
  userId?: string;
}

interface RunSummary {
  run_id: string;
  status?: string;
  created_at?: string;
}

/**
 * BEST-EFFORT defence-in-depth: prepend a restrictive CSP meta to the agent
 * HTML. `connect-src 'none'` discourages network egress (the data is baked in —
 * the dashboard never needs the network). Inline styles/scripts are allowed
 * because a self-contained dashboard vendors them inline.
 *
 * This is NOT a hard security boundary: a `<meta>` CSP can be defeated by an
 * adversarial HTML author (a `<head>` inside a comment, or a `<script>` before
 * the first `<head>`). The REAL containment is the iframe `sandbox="allow-scripts"`
 * (opaque origin: no app cookies/tokens/parent-DOM/navigation). A hard
 * connect-src boundary needs a CSP RESPONSE HEADER on a separate-origin serve
 * path — tracked as a follow-up (needed before 3b's LLM-authored dashboards).
 */
function withCsp(html: string): string {
  const meta =
    '<meta http-equiv="Content-Security-Policy" content="' +
    "default-src 'none'; " +
    "script-src 'unsafe-inline'; " +
    "style-src 'unsafe-inline'; " +
    "img-src data: blob:; font-src data:; " +
    "connect-src 'none'\">";
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (m) => m + meta);
  }
  return `<!doctype html><html><head>${meta}</head><body>${html}</body></html>`;
}

function formatTs(value: string | null | undefined): string {
  if (!value) return '';
  return formatDateTime(value, { invalidFallback: '' });
}

export default function ModelResultsView({ projectId, authToken, userId }: ModelResultsViewProps) {
  const dictWorkspace = useProjectStore((s) => s.dictWorkspace);
  const activeModel = useProcessViewerStore((s) => s.model);
  const generateDashboard = useProcessViewerStore((s) => s.generateDashboard);
  const isGeneratingDashboard = useProcessViewerStore((s) => s.isGeneratingDashboard);
  const dashboardError = useProcessViewerStore((s) => s.dashboardError);

  // The dashboard_html DocFile(s) this production workspace holds. Multi-model
  // workspaces namespace them (dashboard_html__{model_id}); list all + default
  // to the first (stable order by file_id).
  const dashboards = useMemo<DictDocFile[]>(() => {
    const all = Object.values(dictWorkspace?.doc_files ?? {});
    return all
      .filter((f) => f.doc_kind === 'dashboard_html')
      .sort((a, b) => a.file_id.localeCompare(b.file_id));
  }, [dictWorkspace?.doc_files]);

  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  // Free-text ask routed to the visualization subagent (owner #4). On a
  // follow-up it edits the prior authored report rather than starting over.
  const [vizPrompt, setVizPrompt] = useState('');
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  // Bumped by Refresh; a dep of the fetch effect so refresh re-runs the SAME
  // guarded fetch path (no separate imperative fetch that could race + overwrite
  // a newer result).
  const [reloadNonce, setReloadNonce] = useState(0);

  // Keep a valid dashboard selection as docs load / change.
  useEffect(() => {
    if (dashboards.length === 0) {
      if (selectedFileId !== null) setSelectedFileId(null);
      return;
    }
    if (!selectedFileId || !dashboards.some((d) => d.file_id === selectedFileId)) {
      setSelectedFileId(dashboards[0].file_id);
    }
  }, [dashboards, selectedFileId]);

  const selected = dashboards.find((d) => d.file_id === selectedFileId) ?? null;
  const modelId = (selected?.model_refs && selected.model_refs[0]) || null;
  const pinnedRunId = selected?.derived_from_run_id ?? null;
  const workspaceModelId = useMemo(() => {
    if (activeModel?.model_id) return activeModel.model_id;
    const models = Object.values(dictWorkspace?.models ?? {});
    return models[0]?.model_id ?? null;
  }, [activeModel?.model_id, dictWorkspace?.models]);

  // The run actually being rendered defaults to the dashboard's pinned run.
  // `selectedRunId` is reset to null in the dashboard <select> onChange (not an
  // effect), so it can never briefly pair the previous run with a new model.
  const effectiveRunId = selectedRunId ?? pinnedRunId;
  const generationModelId = modelId ?? workspaceModelId;
  const inlineGenerateMessage = generateMessage || dashboardError;

  // Fetch the dashboard HTML for (model, run). The single guarded fetch path —
  // Refresh bumps `reloadNonce` to re-enter it rather than fetching separately.
  useEffect(() => {
    let cancelled = false;
    if (!projectId || !modelId || !authToken) {
      setHtml(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await api.getModelDashboard(projectId, modelId, effectiveRunId, authToken);
        if (cancelled) return;
        setHtml(res.html || '');
      } catch (e: any) {
        if (cancelled) return;
        setHtml(null);
        setError(
          /404/.test(e?.message || '')
            ? 'No dashboard found for this run yet — regenerate it from the latest run.'
            : (e?.message || 'Failed to load dashboard'),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, modelId, effectiveRunId, authToken, reloadNonce]);

  // Load the run list (for the selector) once a model is known.
  useEffect(() => {
    let cancelled = false;
    if (!projectId || !modelId || !authToken) {
      setRuns([]);
      return;
    }
    (async () => {
      try {
        const result = await api.getModelRuns(projectId, modelId, authToken, userId);
        if (cancelled) return;
        const list = (result?.runs || result || []) as RunSummary[];
        setRuns(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setRuns([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, modelId, authToken, userId]);

  // Re-run the guarded fetch effect (no separate imperative fetch → no race).
  const refresh = () => setReloadNonce((n) => n + 1);

  const handleGenerateDashboard = async () => {
    setGenerateMessage(null);
    if (!projectId || !authToken || !generationModelId) {
      setGenerateMessage('No model is available for report visualization.');
      return;
    }
    useProcessViewerStore.setState({
      projectId,
      workspaceId: dictWorkspace?.workspace_id ?? null,
      ...(userId ? { userId } : {}),
    });
    const completed = await generateDashboard(projectId, generationModelId, authToken, effectiveRunId, vizPrompt);
    if (completed) {
      setGenerateMessage(null);
      setReloadNonce((n) => n + 1);
    } else {
      setGenerateMessage(
        useProcessViewerStore.getState().dashboardError
        || 'Could not generate report visualization.',
      );
    }
  };

  // ---- Empty state: no dashboard generated yet -----------------------------
  if (dashboards.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-bg-warm">
        <div className="text-center max-w-md px-6">
          <div className="w-12 h-12 rounded-full bg-surface flex items-center justify-center mx-auto mb-4 border border-border-default">
            <BarChart3 className="w-6 h-6 text-fg-tertiary" />
          </div>
          <h3 className="text-base font-semibold text-fg-secondary mb-1.5">No results report yet</h3>
          <p className="text-sm text-fg-tertiary">
            Run the model, then ask BARK to visualize its output. BARK writes the
            report code from the model&rsquo;s output data and renders it here.
          </p>
          <textarea
            value={vizPrompt}
            onChange={(e) => setVizPrompt(e.target.value)}
            rows={2}
            placeholder="What should the report show? e.g. histogram of balances, WA rate by vintage, and a pool-metrics summary table. (Leave blank for an automatic summary.)"
            className="mt-4 w-full resize-none rounded-md border border-border-default bg-surface px-3 py-2 text-xs text-fg placeholder:text-fg-tertiary focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button
            type="button"
            onClick={handleGenerateDashboard}
            disabled={isGeneratingDashboard || !generationModelId || !projectId || !authToken}
            className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-white bg-accent hover:bg-accent-hover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {isGeneratingDashboard ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <BarChart3 className="w-3.5 h-3.5" />
            )}
            {isGeneratingDashboard ? 'Writing the report…' : 'Generate report'}
          </button>
          {inlineGenerateMessage && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-danger bg-surface rounded-md border border-danger/40 px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{inlineGenerateMessage}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // A dashboard record with no model reference can't be located/served — show
  // an explicit state rather than a silent blank frame. (Only when a doc is
  // actually selected; a transient null selection resolves on the next render.)
  if (selected && !modelId) {
    return (
      <div className="h-full flex items-center justify-center bg-bg-warm">
        <div className="text-center max-w-md px-6">
          <div className="w-12 h-12 rounded-full bg-surface flex items-center justify-center mx-auto mb-4 border border-border-default">
            <AlertCircle className="w-6 h-6 text-warning" />
          </div>
          <h3 className="text-base font-semibold text-fg-secondary mb-1.5">Dashboard not linked to a model</h3>
          <p className="text-sm text-fg-tertiary">
            This dashboard record has no model reference, so its results can&rsquo;t be
            located. Regenerate it with <code className="text-fg-secondary">write_dashboard</code>.
          </p>
        </div>
      </div>
    );
  }

  const isApproved = !!selected?.approved_by;

  return (
    <div className="h-full flex flex-col bg-bg-warm">
      {/* Toolbar: dashboard selector (multi-model) + run selector + refresh */}
      <div className="flex items-center gap-2 px-3 h-11 flex-shrink-0 border-b border-border-default bg-surface">
        {dashboards.length > 1 && (
          <select
            value={selectedFileId ?? ''}
            onChange={(e) => {
              // Switch dashboard AND clear the run pin in the same commit, so the
              // next render never pairs the old run with the new model.
              setSelectedFileId(e.target.value);
              setSelectedRunId(null);
            }}
            className="h-7 px-2 rounded-md text-xs font-medium bg-surface text-fg border border-border-default hover:bg-bg-warm"
          >
            {dashboards.map((d) => (
              <option key={d.file_id} value={d.file_id}>
                {d.title || d.file_id}
              </option>
            ))}
          </select>
        )}
        <label className="text-xs text-fg-tertiary">Run</label>
        <select
          value={effectiveRunId ?? ''}
          onChange={(e) => setSelectedRunId(e.target.value || null)}
          className="h-7 px-2 rounded-md text-xs font-medium bg-surface text-fg border border-border-default hover:bg-bg-warm max-w-[280px]"
        >
          {pinnedRunId && !runs.some((r) => r.run_id === pinnedRunId) && (
            <option value={pinnedRunId}>{pinnedRunId} (current)</option>
          )}
          {runs.map((r) => (
            <option key={r.run_id} value={r.run_id}>
              {r.run_id}
              {r.status ? ` · ${r.status}` : ''}
              {r.created_at ? ` · ${formatTs(r.created_at)}` : ''}
            </option>
          ))}
        </select>
        <div className="flex-1" />
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-semibold text-fg-secondary border border-border-default bg-surface hover:bg-bg-warm transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Ask bar — author / refine the report on request (owner #4). */}
      <div className="flex items-center gap-2 px-3 py-2 flex-shrink-0 border-b border-border-default bg-surface">
        <input
          type="text"
          value={vizPrompt}
          onChange={(e) => setVizPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !isGeneratingDashboard) handleGenerateDashboard(); }}
          placeholder="Ask BARK to change the report — e.g. make the histogram log-scale and add a top-10-by-balance table"
          className="flex-1 h-7 px-2.5 rounded-md text-xs text-fg bg-surface border border-border-default placeholder:text-fg-tertiary focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <button
          type="button"
          onClick={handleGenerateDashboard}
          disabled={isGeneratingDashboard || !generationModelId || !projectId || !authToken}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-semibold text-white bg-accent hover:bg-accent-hover disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          {isGeneratingDashboard ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <BarChart3 className="w-3.5 h-3.5" />
          )}
          {isGeneratingDashboard ? 'Writing…' : 'Update report'}
        </button>
      </div>
      {inlineGenerateMessage && (
        <div className="flex items-center gap-2 px-3 py-2 flex-shrink-0 border-b border-border-default bg-surface text-xs text-danger">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{inlineGenerateMessage}</span>
        </div>
      )}

      {/* The sandboxed dashboard frame */}
      <div className="flex-1 min-h-0 relative bg-white">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/60 z-10">
            <Loader2 className="w-5 h-5 text-fg-tertiary animate-spin" />
          </div>
        )}
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-2 text-sm text-danger bg-surface rounded-md border border-danger/40 px-4 py-3 max-w-md">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        ) : html !== null ? (
          <iframe
            key={`${selectedFileId}:${effectiveRunId}`}
            title="Model results dashboard"
            // SECURITY: allow-scripts ONLY — never allow-same-origin (that would
            // grant the agent HTML access to app cookies/tokens/parent DOM).
            sandbox="allow-scripts"
            srcDoc={withCsp(html)}
            className="w-full h-full border-0 bg-white"
          />
        ) : null}
      </div>

      {/* Reproducibility footer */}
      <div className="flex items-center gap-3 px-3 h-8 flex-shrink-0 border-t border-border-default bg-surface text-[11px] text-fg-tertiary">
        {modelId && <span>model <span className="text-fg-secondary">{modelId}</span></span>}
        {effectiveRunId && <span>· run <span className="text-fg-secondary">{effectiveRunId}</span></span>}
        {selected?.derived_from_model_version != null && (
          <span>· v{selected.derived_from_model_version}</span>
        )}
        {selected?.dashboard_artifact_sha256 && (
          <span>· {selected.dashboard_artifact_sha256.replace('sha256:', '').slice(0, 12)}</span>
        )}
        <div className="flex-1" />
        {isApproved ? (
          <span className="inline-flex items-center gap-1 text-success">
            <ShieldCheck className="w-3.5 h-3.5" /> approved
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-warning">
            <Clock className="w-3.5 h-3.5" /> needs review
          </span>
        )}
      </div>
    </div>
  );
}
